import React, {useMemo, useState} from 'react';
import styles from './BlockStoriesTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {Pencil, TrashBin} from '@gravity-ui/icons';
import {DataTable} from '../../shared';
import {StoryModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {EditBlockStoryModal, DeleteStoryModal} from '../../features';
import {useNavigate} from 'react-router';

interface BlockStoriesTableProps {
  stories: StoryModel[];
  blockId: string;
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const BlockStoriesTable = ({
  stories,
  blockId,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: BlockStoriesTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingStoryId, setEditingStoryId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingStoryId, setDeletingStoryId] = useState<string | null>(null);
  const [editingStory, setEditingStory] = useState<StoryModel | null>(null);

  const handleRowClick = (story: StoryModel) => {
    navigate(`/content/blocks/${blockId}/stories/${story.id}`);
  };

  const handleEditStory = (story: StoryModel) => {
    setEditingStory(story);
    setEditingStoryId(story.id);
    setIsEditModalOpen(true);
  };

  const handleDeleteStory = (story: StoryModel) => {
    setDeletingStoryId(story.id);
    setIsDeleteModalOpen(true);
  };

  const columns = useMemo<TableColumnConfig<StoryModel>[]>(
    () => [
      {
        id: 'title',
        name: 'Заголовок истории',
        template: (item: StoryModel) => <Text variant="body-1">{item.title}</Text>,
      },
      {
        id: 'priority',
        name: 'Приоритет',
        template: (item: StoryModel) => <Text variant="body-1">{item.priority}</Text>,
      },
      {
        id: 'more',
        name: () => <div className={styles.tableHeader}></div>,
        template: (item: StoryModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={Pencil} />,
                  action: () => handleEditStory(item),
                  text: 'Редактировать',
                  className: styles.dropdownItem,
                },
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => handleDeleteStory(item),
                  text: 'Удалить',
                  className: styles.dropdownItemDelete,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<StoryModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={stories || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="История не найдена"
        emptyStateDescription="История с таким названием не найдена. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}

      {editingStory && (
        <EditBlockStoryModal
          story={editingStory}
          open={isEditModalOpen}
          setOpen={setIsEditModalOpen}
          blockId={blockId}
          storyId={editingStoryId}
        />
      )}

      {deletingStoryId && (
        <DeleteStoryModal
          open={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          blockId={blockId}
          storyId={deletingStoryId}
        />
      )}
    </div>
  );
};
